import React from 'react';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { phasePaddingX } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { RectReadOnly } from 'react-use-measure';

interface CurveLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

const getPipelineAnalysisCurveBoundingBoxSize = (props: {
  point1: {
    x: number;
    y: number;
  };
  point2: {
    x: number;
    y: number;
  };
  thickness: number;
}): {
  width: number;
  height: number;
} => {
  const { point1, point2, thickness } = props;

  const leftPoint = point1.x < point2.x ? point1 : point2;
  const rightPoint = point1.x < point2.x ? point2 : point1;

  const width = rightPoint.x - leftPoint.x;

  // Our stroke is centered, so we need to offset the y values by half the thickness.
  const adjustedHeight = Math.abs(rightPoint.y - leftPoint.y) + thickness;

  return {
    width,
    height: adjustedHeight,
  };
};

const DynamicPipelineAnalysisCurve = (props: {
  point1: {
    x: number;
    y: number;
  };
  point2: {
    x: number;
    y: number;
  };
  colorClass: string;
  lightened: boolean;
  thickness: number;
}) => {
  const { point1, point2, thickness, colorClass, lightened } = props;

  const leftPoint = point1.x < point2.x ? point1 : point2;
  const rightPoint = point1.x < point2.x ? point2 : point1;

  const leftPointIsTop = leftPoint.y < rightPoint.y;
  const rightPointIsTop = !leftPointIsTop;

  const height = Math.abs(rightPoint.y - leftPoint.y);

  const size = getPipelineAnalysisCurveBoundingBoxSize({
    point1,
    point2,
    thickness,
  });

  const { width, height: adjustedHeight } = size;
  // We are allowing users to pass in an arbitrary position, so let's bound it to the left point.
  const effectivePoint1 = {
    x: 0,
    y: leftPointIsTop ? 0 : height,
  };

  const effectivePoint2 = {
    x: width,
    y: rightPointIsTop ? 0 : height,
  };

  const { x: x1, y: y1BeforeThicknessOffset } = effectivePoint1;
  const { x: x2, y: y2BeforeThicknessOffset } = effectivePoint2;

  const y1 = y1BeforeThicknessOffset + thickness / 2;
  const y2 = y2BeforeThicknessOffset + thickness / 2;

  const midPointX = (x1 + x2) / 2;
  const midPointY = (y1 + y2) / 2;

  // Based on the Figma, it seems like the control point is exactly 20.64% of the way from the left
  const lengthBeforeControlPoint = width * 0.2064;

  // Once we hit the control points, it basically becomes a straight line.
  const controlPointX1 = x1 + lengthBeforeControlPoint;
  const controlPointY1 = y1;

  const controlPointX2 = x2 - lengthBeforeControlPoint;
  const controlPointY2 = y2;

  const d = `M ${x1} ${y1} C ${controlPointX1} ${controlPointY1}, ${controlPointX1} ${controlPointY1}, ${midPointX} ${midPointY} C ${controlPointX2} ${controlPointY2}, ${controlPointX2} ${controlPointY2}, ${x2} ${y2}`;

  return (
    <span className={colorClass}>
      <svg
        height={adjustedHeight}
        width={width}
        viewBox={`0 0 ${width} ${adjustedHeight}`}
      >
        <path
          d={d}
          fill="none"
          stroke={'currentColor'}
          strokeOpacity={lightened ? '0.2' : '0.8'}
          strokeWidth={thickness}
        />
        <path
          d={d}
          fill="none"
          stroke="url(#gradient)"
          strokeOpacity="0.1"
          strokeWidth={thickness}
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  );
};

export const Curve = (props: {
  layoutMeasurements: RectReadOnly;
  nextLayoutMeasurements: RectReadOnly;
  color: string;
  lightened: boolean;
  gap: number;
}) => {
  const { layoutMeasurements, nextLayoutMeasurements, color, lightened, gap } =
    props;

  const point1 = {
    x: layoutMeasurements.left + layoutMeasurements.width,
    y: layoutMeasurements.top + layoutMeasurements.height / 2,
  };

  const point2 = {
    x: nextLayoutMeasurements.left,
    y: nextLayoutMeasurements.top + nextLayoutMeasurements.height / 2,
  };

  const thickness = Math.min(
    layoutMeasurements.height - 24,
    nextLayoutMeasurements.height - 24,
    gap * 0.4,
  );

  const { height, width } = getPipelineAnalysisCurveBoundingBoxSize({
    point1,
    point2,
    thickness,
  });

  const goingUp = point1.y > point2.y;

  const styleProps: React.CSSProperties = {
    left: `${layoutMeasurements.width - 1}px`,
    width: `${width}px`,
    height: `${height}px`,
  };

  if (goingUp) {
    styleProps.bottom = `${layoutMeasurements.height / 2 - thickness / 2 - 1}px`;
  } else {
    styleProps.top = `${layoutMeasurements.height / 2 - thickness / 2 - 1}px`;
  }

  const colorClass = (color as Color) ?? 'Gray';

  return (
    <div className="absolute" style={styleProps}>
      <DynamicPipelineAnalysisCurve
        thickness={thickness}
        point1={point1}
        point2={point2}
        colorClass={ConfigColors[colorClass].Primary.text}
        lightened={lightened}
      />
    </div>
  );
};
